// A tiny global guard for "leaving will drop the Claude connection". A live
// brainstorm binds exactly one browser session to the gateway's single ephemeral
// round; navigating away (switching sessions in the sidebar, New session, a nav
// link, a reload) ends that round — the agent stops listening. So before any such
// navigation we confirm, and a `beforeunload` covers tab-close / reload.
//
// The controller marks the guard active while its bridge round is live (set on
// the first `live`, cleared on stop/dispose). Navigation entry points call
// `confirmLeave()`; it resolves true immediately when inactive, otherwise opens
// the confirm dialog (App renders <LeaveGuardDialog>) and resolves on the choice.

type Resolver = (ok: boolean) => void

let active = false
let pending: Resolver | null = null
const listeners = new Set<() => void>()

function emit(): void {
  for (const l of listeners) l()
}

/** The controller flips this while a brainstorm round is live. */
export function setLeaveGuardActive(value: boolean): void {
  if (active === value) return
  active = value
  emit()
}

export function isLeaveGuardActive(): boolean {
  return active
}

/** Resolve true (safe to leave) when inactive; otherwise open the dialog and
 *  resolve on Confirm/Cancel. A second call while one is pending cancels the
 *  first (the latest navigation intent wins). */
export function confirmLeave(): Promise<boolean> {
  if (!active) return Promise.resolve(true)
  if (pending) {
    const prev = pending
    pending = null
    prev(false)
  }
  return new Promise<boolean>((resolve) => {
    pending = resolve
    emit()
  })
}

/** The dialog calls this with the user's choice. */
export function resolveLeave(ok: boolean): void {
  const p = pending
  pending = null
  emit()
  p?.(ok)
}

export function hasPendingLeave(): boolean {
  return pending !== null
}

export function subscribeLeaveGuard(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}
